// ink-component
import React, { ReactNode } from 'react';
import { ThemeProvider } from 'styled-components';

import getGlobalProps from '../../helpers/constants/global-props';
import useWindowWidth from '../../hooks/useWindowWidth';

import {
  Attachment,
  Control,
  ErrorWrapper,
  FieldControl,
  FieldHelpWrapper,
  InfoWrapper,
  Label,
  LabelWrapper,
  Required,
} from './styles';

import HStack from '../HStack/HStack';
import Row from '../Grid/Row/Row';
import Column from '../Grid/Column/Column';
import Controller from '../_common/Controller';
import ErrorContext, { getErrorMessageId } from '../_common/Controller/errorContext';
import { FieldHelpText } from '../Help/styles';

export interface FieldProps {
  /** One or more input Components - Select, Input, DatePicker, ... */
  children?: ReactNode;
  /** Label of the field */
  label?: ReactNode;
  /** Error contents */
  error?: ReactNode;
  /** Help contents */
  help?: ReactNode;
  /** Info contents */
  info?: ReactNode;
  /** Modifier for required */
  isRequired?: boolean;
  /** Modifier for stacked */
  isStacked?: boolean;
  /** Modifier for full width */
  isFull?: boolean;
  /** Native htmlFor prop passed to label */
  htmlFor?: string;
  /** Add invalid styles to child inputs automatically */
  errorAutoInvalidate?: boolean;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
  /** @ignore */
  density?: 'low' | 'high';
  /** @ignore */
  attachment?: ReactNode;
  /** @ignore */
  legacyHelp?: boolean;
}

/**
 * @purpose Provides label, required tag, help, info and error text to form items
 * @categories form
 * @aka label
 */
const Field = (props: FieldProps) => {
  const {
    children,
    label,
    error,
    help,
    info,
    isRequired,
    isStacked,
    isFull,
    attachment,
    htmlFor,
    density,
    legacyHelp,
    errorAutoInvalidate,
  } = props;

  // Once we are css-detached, we should look into refactoring this to use Grid.
  const { isMobile } = useWindowWidth();

  const fieldTheme = {
    fieldRequired: isRequired,
    fieldStacked: isStacked,
    fieldFull: isFull,
    fieldMobile: isMobile,
    fieldError: !!error,
    fieldLegacyHelp: legacyHelp,
    fieldDensity: density,
  };

  const getHtmlFor = () => {
    if (htmlFor) {
      return htmlFor;
    }
    if (children) {
      // @ts-ignore
      return React.Children.map(children, child => child && child.props && child.props.id)[0];
    }
    return '';
  };

  const errorContext = {
    hasError: !!error,
    errorAutoInvalidate: !!errorAutoInvalidate,
    errorMessageId: error ? getErrorMessageId() : '',
  };

  const renderedChildren = (
    // Informs inputs down the tree about Field level errors
    <ErrorContext.Provider value={errorContext}>
      {Boolean(help) && !legacyHelp && (isStacked || isMobile) && (
        <FieldHelpWrapper>
          <FieldHelpText>{help}</FieldHelpText>
        </FieldHelpWrapper>
      )}

      <Control>{children}</Control>

      {Boolean(attachment) && <Attachment>{attachment}</Attachment>}

      {Boolean(error) && (
        <div role="alert" aria-live="polite" id={errorContext.errorMessageId}>
          <ErrorWrapper>
            <Controller.Error>{error}</Controller.Error>
          </ErrorWrapper>
        </div>
      )}

      {Boolean(help) && (legacyHelp || (!isStacked && !isMobile)) && (
        <FieldHelpWrapper>
          <FieldHelpText>{help}</FieldHelpText>
        </FieldHelpWrapper>
      )}

      {Boolean(info) && <InfoWrapper>{info}</InfoWrapper>}
    </ErrorContext.Provider>
  );

  let columnProps;
  let hstackProps;
  if (isStacked || isMobile) {
    columnProps = { mobile: '12-of-12' };
  } else {
    columnProps = { mobile: '4-of-12' };
    hstackProps = { align: 'right' };
  }

  const globalProps = getGlobalProps({});

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        ...fieldTheme,
      })}
    >
      <FieldControl data-testid={props['data-testid']} {...globalProps}>
        <Row>
          <Column {...columnProps}>
            {Boolean(label) && (
              <HStack {...hstackProps}>
                <LabelWrapper>
                  <Label htmlFor={getHtmlFor()}>{label}</Label>
                  {isRequired && <Required title="Required" />}
                </LabelWrapper>
              </HStack>
            )}
            {Boolean(isStacked || isMobile) && renderedChildren}
          </Column>
          {Boolean(!isStacked && !isMobile) && <Column mobile="8-of-12">{renderedChildren}</Column>}
        </Row>
      </FieldControl>
    </ThemeProvider>
  );
};

Field.defaultProps = {
  isRequired: false,
  isStacked: false,
  isFull: false,
  legacyHelp: false,
  errorAutoInvalidate: true,
};

Field.displayName = 'Field';

export default Field;
